/**
 * useDepartments + department-management mutations — extracted from
 * `use-users.ts` per issue #134 (split monolithic Users hook).
 *
 * Covers:
 *   - Department list: useDepartments, DepartmentRow
 *   - Department CRUD: useCreateDepartment, useUpdateDepartment,
 *     useDeleteDepartment
 *
 * The shapes + behaviour are unchanged from the pre-split version. All
 * consumers continue to import from `lib/use-users` (which re-exports
 * everything here) — direct imports from `lib/use-departments` are also
 * supported for new code.
 */

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { apiClient } from './api-client';

// ─── B2b-T3 — how a person's departments are RENDERED, in one place ────────

/**
 * The departments one person is in, as the API serves them.
 *
 * Mirrors `UserDepartmentsSchema` in packages/shared/src/schemas/departments.ts
 * — the server's shape, rendered here and never recomputed.
 */
export interface UserDepartments {
  all: boolean;
  items: { id: string; name: string }[];
}

/**
 * THE ONE RENDERING of a person's departments, for every screen that prints
 * them: the roster cell, the roster's sort key and search blob, the detail
 * page's Department field and header chip, and the wizard's summary row.
 *
 * WHY IT IS A FUNCTION AND NOT FOUR STRING LITERALS. "All departments" is a
 * SENTENCE about a state the store holds (`all_departments` true, no membership
 * rows), and "—" is a DIFFERENT state (no departments, company-wide items
 * only). Four screens writing their own version of that distinction is four
 * chances for one of them to render an all-departments person as an empty cell,
 * which reads as the most restricted person in the tenant when they are the
 * least.
 *
 * @param placeholder what to show for somebody in no department at all.
 */
export function formatUserDepartments(
  departments: UserDepartments | null | undefined,
  placeholder = '—',
): string {
  if (!departments) return placeholder;
  if (departments.all) return 'All departments';
  if (departments.items.length === 0) return placeholder;
  return departments.items.map((d) => d.name).join(', ');
}

/**
 * Is this person a MEMBER of the department with this id?
 *
 * THE FILTER'S RULE, and it is no longer `u.department === id`: a person holds
 * a SET of departments, so the test is membership of the set.
 *
 * AN ALL-DEPARTMENTS PERSON IS NOT A MEMBER OF ANY DEPARTMENT, and that is
 * deliberate rather than an oversight. It is the answer the SERVER gives —
 * `departmentMemberSql` (packages/api/src/policy/principal.ts) is an EXISTS
 * over membership ROWS, and an all-departments person has none — and it is the
 * answer the Departments page's member counts and the delete guard give, all
 * from the same rows. The roster sends this filter to the server whenever the
 * value is a real department id (it has to: the list paginates), so a
 * client-side rule that disagreed would mean the same filter returned different
 * people depending on how many users the tenant has. What an all-departments
 * person may SEE is a different question with a different reader — `canSee` in
 * packages/api/src/policy/visibility.ts — and this is not it.
 */
export function isInDepartment(
  departments: UserDepartments | null | undefined,
  departmentId: string,
): boolean {
  if (!departments) return false;
  return departments.items.some((d) => d.id === departmentId);
}

// ─── W1a-08 — Departments tab ──────────────────────────────────────────────

/**
 * Wire-shape for a single row from `GET /api/v1/users/departments`.
 *
 * Departments form an organizational tree within a tenant.
 *
 * B2b-T3 — `memberCount` is a REAL per-department number now: how many people
 * in this tenant hold a membership row for it, counted once each
 * (`resolveDepartmentMemberCounts` in packages/api/src/policy/principal.ts).
 * These numbers MUST NOT be summed into a tenant total — a person in two
 * departments is counted in both, correctly, and the tenant figure is the
 * separate `assignedMemberCount` the list returns.
 *
 * `parentName` and `headUserName` are denormalized server-side joins so
 * the table can render without a second round-trip per row.
 */
export interface DepartmentRow {
  id: string;
  name: string;
  description: string | null;
  parentId: string | null;
  parentName: string | null;
  headUserId: string | null;
  headUserName: string | null;
  memberCount: number;
  createdAt: string;
  updatedAt: string;
}

interface DepartmentsListResponse {
  data: DepartmentRow[];
  /**
   * B2b-T3 — the TENANT-WIDE figure, served rather than summed. The screen
   * used to add up the page's `memberCount`s, which counts a person in two
   * departments twice and only ever saw the current page.
   */
  summary: { assignedMemberCount: number };
  pagination: {
    page: number;
    pageSize: number;
    totalCount: number;
    totalPages: number;
  };
}

export interface UseDepartmentsFilters {
  page?: number;
  pageSize?: number;
  search?: string;
}

/**
 * Fetch the department list for the caller's tenant. Optional `search`
 * filters on name + description (ILIKE).
 *
 * Query key: `['departments', filters]`. Mutations invalidate `['departments']`.
 */
export function useDepartments(filters: UseDepartmentsFilters = {}): {
  departments: DepartmentRow[];
  /**
   * B2b-T3 — people in this tenant who are in at least one department,
   * counted once each. `null` until the list has loaded; the screen renders
   * nothing rather than a zero it made up.
   */
  assignedMemberCount: number | null;
  pagination: DepartmentsListResponse['pagination'] | null;
  isLoading: boolean;
  error: unknown;
  refetch: () => void;
} {
  const params = new URLSearchParams();
  if (filters.page) params.set('page', String(filters.page));
  if (filters.pageSize) params.set('pageSize', String(filters.pageSize));
  if (filters.search) params.set('search', filters.search);
  const query = params.toString();
  const path = query ? `/users/departments?${query}` : '/users/departments';

  const { data, isLoading, error, refetch } = useQuery<DepartmentsListResponse>({
    queryKey: ['departments', filters],
    queryFn: () => apiClient.get<DepartmentsListResponse>(path),
    staleTime: 60_000,
  });

  return {
    departments: data?.data ?? [],
    assignedMemberCount:
      typeof data?.summary?.assignedMemberCount === 'number'
        ? data.summary.assignedMemberCount
        : null,
    pagination: data?.pagination ?? null,
    isLoading,
    error,
    refetch,
  };
}

/**
 * The API's maximum `pageSize` for `GET /users/departments` (users.ts:
 * `pageSize: z.coerce.number().int().min(1).max(200).default(50)`). There is no
 * value that fetches more in one request, which is why `useAllDepartments`
 * below pages instead of asking for a bigger number.
 */
const DEPARTMENTS_MAX_PAGE_SIZE = 200;

/**
 * A ceiling on the paging loop, so a server that reported a wrong `totalPages`
 * cannot spin this hook forever. 50 pages × 200 = 10,000 departments, well past
 * anything an org chart holds.
 */
const DEPARTMENTS_MAX_PAGES = 50;

/**
 * EVERY department in the tenant — for the controls that WRITE a person's
 * scope, not the ones that display a page of them.
 *
 * Review BF-B2bT3-08. Both pickers (create wizard, edit dialog) and the
 * roster's Department filter asked for `pageSize: 200`, the API's maximum, with
 * no paging and no search inside the popover. A tenant with 250 departments had
 * fifty nobody could assign anyone to from the Users rail — and a person
 * already in one of them (assigned by the import, which resolves by NAME and is
 * not capped) showed as "N selected" in a picker that could not name what it
 * was about to overwrite. A picker that cannot show a department is a picker
 * that silently narrows what an admin may choose, so it pages to the end.
 *
 * The page LIST is a different question with a different answer: the Departments
 * tab paginates on purpose and uses `useDepartments` above.
 */
export function useAllDepartments(): {
  departments: DepartmentRow[];
  /** See `useDepartments` — the tenant-wide figure, `null` until loaded. */
  assignedMemberCount: number | null;
  /** True while any page is still in flight. */
  isLoading: boolean;
  /**
   * True when the ceiling above stopped the loop before the last page — the
   * list is INCOMPLETE and a caller that writes a scope should say so.
   */
  truncated: boolean;
  error: unknown;
  refetch: () => void;
} {
  const { data, isLoading, error, refetch } = useQuery<{
    rows: DepartmentRow[];
    assignedMemberCount: number | null;
    truncated: boolean;
  }>({
    // Under the same `['departments']` prefix every mutation invalidates, so a
    // rename on the Departments tab reaches the pickers too.
    queryKey: ['departments', { all: true }],
    queryFn: async () => {
      const rows: DepartmentRow[] = [];
      let assignedMemberCount: number | null = null;
      let page = 1;
      let totalPages = 1;
      let truncated = false;
      for (;;) {
        const params = new URLSearchParams({
          page: String(page),
          pageSize: String(DEPARTMENTS_MAX_PAGE_SIZE),
        });
        const resp = await apiClient.get<DepartmentsListResponse>(
          `/users/departments?${params.toString()}`,
        );
        rows.push(...(resp.data ?? []));
        if (typeof resp.summary?.assignedMemberCount === 'number') {
          assignedMemberCount = resp.summary.assignedMemberCount;
        }
        totalPages = resp.pagination?.totalPages ?? 1;
        if (page >= totalPages) break;
        if (page >= DEPARTMENTS_MAX_PAGES) {
          truncated = true;
          break;
        }
        page += 1;
      }
      return { rows, assignedMemberCount, truncated };
    },
    staleTime: 60_000,
  });

  return {
    departments: data?.rows ?? [],
    assignedMemberCount: data?.assignedMemberCount ?? null,
    isLoading,
    truncated: data?.truncated === true,
    error,
    refetch,
  };
}

/**
 * Body for `POST /api/v1/users/departments`. Server enforces:
 *   - `name`: 1-200 chars, trimmed
 *   - case-insensitive name uniqueness within tenant (409 NAME_CONFLICT)
 *   - `parentId` (if given) must belong to caller's tenant (400 INVALID_PARENT)
 *   - `headUserId` (if given) must belong to caller's tenant (400 INVALID_HEAD_USER)
 */
export interface CreateDepartmentBody {
  name: string;
  description?: string | null;
  parentId?: string | null;
  headUserId?: string | null;
}

/**
 * Create a department — wires to W1a-08's POST /users/departments.
 */
export function useCreateDepartment() {
  const qc = useQueryClient();
  return useMutation<DepartmentRow, Error, CreateDepartmentBody>({
    mutationFn: (body) => apiClient.post<DepartmentRow>('/users/departments', body),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['departments'] });
    },
  });
}

/**
 * Body for `PATCH /api/v1/users/departments/:id`. All fields optional but
 * at least one must be present (else 422 VALIDATION_ERROR). Same name +
 * parent + head-user validations as POST. Self-cycle and deeper-cycle
 * parents return 400 INVALID_PARENT.
 *
 * No-op short-circuit: if every field equals the existing value, the
 * server returns 200 with the existing row but emits NO new audit row.
 */
export interface UpdateDepartmentBody {
  name?: string;
  description?: string | null;
  parentId?: string | null;
  headUserId?: string | null;
}

/**
 * Update a department — wires to W1a-08's PATCH /users/departments/:id.
 */
export function useUpdateDepartment() {
  const qc = useQueryClient();
  return useMutation<
    DepartmentRow,
    Error,
    { departmentId: string; body: UpdateDepartmentBody }
  >({
    mutationFn: ({ departmentId, body }) =>
      apiClient.patch<DepartmentRow>(`/users/departments/${departmentId}`, body),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['departments'] });
    },
  });
}

/**
 * Delete a department — wires to W1a-08's DELETE /users/departments/:id.
 *
 * 409 DEPARTMENT_HAS_CHILDREN if the department has child departments.
 * (Future: 409 DEPARTMENT_IN_USE once F-W1a-08.2 lands the user-to-
 * department association and `memberCount > 0` blocks delete.)
 */
export function useDeleteDepartment() {
  const qc = useQueryClient();
  return useMutation<{ id: string; deleted: true }, Error, { departmentId: string }>({
    mutationFn: ({ departmentId }) =>
      apiClient.delete<{ id: string; deleted: true }>(
        `/users/departments/${departmentId}`
      ),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['departments'] });
    },
  });
}
